import React, { HTMLAttributes, useEffect, useRef, useState } from 'react';
import { Button, Input, Message, Popover, Select } from '@meitu/saas-ui';
import { parseToPixels, parseToUnit, SizeUnitType } from '@meitu/saas-editor-core/es/utils/unit-kit';
import { useLocaleStore } from '@/i18n';
import SizeInfo from '../size-info';
import './index.less';
import clamp from 'lodash/clamp';
import {
    ISize,
    MAX_STAGE_HEIGHT,
    MAX_STAGE_WIDTH,
    MIN_STAGE_HEIGHT,
    MIN_STAGE_WIDTH,
} from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import { editor } from '@/instance';

interface IProps extends HTMLAttributes<HTMLDivElement> {
    open: boolean;
    className?: string;
    scrollClassName?: string;
    children: React.ReactNode;
    onClose: () => void;
    onSave?: (value: string, width: number, height: number, unit: SizeUnitType) => void;
    currentWidth: number;
    currentHeight: number;
}
const AddPopover: React.FC<IProps> = (props) => {
    const { open, children, className, scrollClassName, onClose, onSave, currentWidth, currentHeight } = props;
    const { $t } = useLocaleStore();
    const { Option } = Select;
    const [keyword, setKeyword] = useState<string>('');
    const [lockRatio, setLockRatio] = useState(true);
    const [width, setWidth] = useState(currentWidth);
    const [height, setHeight] = useState(currentHeight);
    const [unit, setUnit] = useState(SizeUnitType.px);
    const [text, setText] = useState('');
    const originInfo = useRef<ISize>();
    const curPxSize = useRef({ width: currentWidth, height: currentHeight });
    // 初始化面板数据
    useEffect(() => {
        originInfo.current = {
            ...editor.services.frame.getActiveFrameSize(),
        };
    }, []);

    //改变舞台宽高 需要进行替换
    useEffect(() => {
        setWidth(currentWidth);
        setHeight(currentHeight);
        setUnit(SizeUnitType.px);
        curPxSize.current = { width: currentWidth, height: currentHeight };
    }, [currentWidth, currentHeight]);

    // 宽度改变
    const changeWidthHandle = (value: number) => {
        if (!originInfo.current) return;
        const { width: stageWidth, height: stageHeight } = originInfo.current;
        const targetValue = parseToPixels(value, unit);
        if (targetValue > MAX_STAGE_WIDTH || targetValue < MIN_STAGE_WIDTH) {
            Message.error($t((v) => v.panel.backgroundPanel.messageError.autoAdjustedToLimit));
        }
        let destWidth = clamp(targetValue, MIN_STAGE_WIDTH, MAX_STAGE_WIDTH);
        let destHeight = height;
        if (lockRatio) {
            const ratio = stageWidth / stageHeight;
            destHeight = clamp(Math.ceil(destWidth / ratio), MIN_STAGE_WIDTH, MAX_STAGE_WIDTH);
            Object.assign(curPxSize.current, { height: destHeight });
            destHeight = parseToUnit(destHeight, unit);
        }
        Object.assign(curPxSize.current, { width: destWidth });
        destWidth = parseToUnit(destWidth, unit);

        return { width: destWidth, height: destHeight };
    };

    // 高度改变
    const changeHeightHandle = (value: number) => {
        if (!originInfo.current) return;
        const { width: stageWidth, height: stageHeight } = originInfo.current;
        const targetValue = parseToPixels(value, unit);
        if (targetValue > MAX_STAGE_HEIGHT || targetValue < MIN_STAGE_HEIGHT) {
            Message.error($t((v) => v.panel.backgroundPanel.messageError.autoAdjustedToLimit));
        }

        let destHeight = clamp(targetValue, MIN_STAGE_HEIGHT, MAX_STAGE_HEIGHT); //单位：px
        let destWidth = width; //实际单位

        if (lockRatio) {
            const ratio = stageWidth / stageHeight;
            destWidth = clamp(Math.ceil(destHeight * ratio), MIN_STAGE_HEIGHT, MAX_STAGE_HEIGHT);
            Object.assign(curPxSize.current, { width: destWidth }); //单位：px
            destWidth = parseToUnit(destWidth, unit);
        }
        Object.assign(curPxSize.current, { height: destHeight });
        destHeight = parseToUnit(destHeight, unit);

        return { width: destWidth, height: destHeight };
    };

    // 监听输入框值变化
    const keywordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.trim();
        if (value === keyword) return;
        setKeyword(value);
    };

    const handleChangeSize = (value: number, type: 'width' | 'height') => {
        let sizeObj;
        if (type === 'width') {
            sizeObj = changeWidthHandle(value);
        } else {
            sizeObj = changeHeightHandle(value);
        }

        if (sizeObj) {
            const { width, height } = sizeObj;
            setWidth(width);
            setHeight(height);
        }
    };

    const handleBtnClick = () => {
        if (!width || !height) return setText('请输入正确宽高哦~');
        setText('');
        onSave?.(keyword || $t((v) => v.panel.backgroundPanel.customSize), width, height, unit);
        onClose?.();
        reset();
    };

    const reset = () => {
        setKeyword('');
        setWidth(currentWidth);
        setHeight(currentHeight);
    };
    const handleChangeUnit = (val: SizeUnitType) => {
        if (unit === val) return;

        //其他转px
        if (val === SizeUnitType.px) {
            setWidth(curPxSize.current.width);
            setHeight(curPxSize.current.height);
        } else {
            //px转其他
            // 其他转其他
            const parseWidth = parseToUnit(curPxSize.current.width, val);
            const parseHeight = parseToUnit(curPxSize.current.height, val);
            setWidth(parseWidth);
            setHeight(parseHeight);
        }

        setUnit(val);
    };

    useEffect(() => {
        const handleClick = (e: any) => {
            const wrapperEl = document.querySelector(`.${className}`);
            if (wrapperEl && !wrapperEl.contains(e.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClick);

        const handleScroll = () => {
            onClose();
        };
        const el = document.querySelector(`.${scrollClassName}`)?.firstChild;

        el?.addEventListener('scroll', handleScroll);

        return () => {
            el?.removeEventListener('scroll', handleScroll);
            document.removeEventListener('mousedown', handleClick);
        };
    }, []);

    return (
        <Popover
            open={open}
            placement="leftTop"
            overlayClassName={className}
            showArrow={false}
            trigger="click"
            content={
                <div className="adjust-size-popover">
                    <div className="adjust-size-popover__header">
                        <div className="adjust-size-popover__header-title">
                            {$t((v) => v.panel.backgroundPanel.tips.addToMySize)}
                        </div>
                        <div className="adjust-size-popover__header-close">
                            <IconFont name="CrossBlack" fontSize={12} onClick={onClose}></IconFont>
                        </div>
                    </div>

                    <div className="adjust-size-popover__input">
                        <Input
                            placeholder={$t((v) => v.panel.backgroundPanel.tips.inputSizeName)}
                            onChange={keywordChange}
                            value={keyword}
                            maxLength={6}
                        />
                    </div>

                    <div className="adjust-size-popover__size">
                        <SizeInfo
                            widthSize={width}
                            heightSize={height}
                            unit={unit}
                            size="small"
                            lockRatio={lockRatio}
                            onChangeHeight={(val) => handleChangeSize(val, 'height')}
                            onChangeWidth={(val) => handleChangeSize(val, 'width')}
                            onChangeLock={() => {
                                setLockRatio(!lockRatio);
                            }}
                        />

                        <Select
                            style={{ marginLeft: '4px' }}
                            popupClassName="adjust-size-select"
                            defaultValue={SizeUnitType.px}
                            onChange={handleChangeUnit}
                            value={unit}
                            suffixIcon={<IconFont name="ChevronDownBlack" fontSize={10} color="var(--content-input)" />}
                            getPopupContainer={(el) => el.parentNode as HTMLElement}
                        >
                            <Option key={SizeUnitType.px} value={SizeUnitType.px}>
                                {SizeUnitType.px}
                            </Option>
                            <Option key={SizeUnitType.mm} value={SizeUnitType.mm}>
                                {SizeUnitType.mm}
                            </Option>
                            <Option key={SizeUnitType.cm} value={SizeUnitType.cm}>
                                {SizeUnitType.cm}
                            </Option>
                        </Select>
                    </div>
                    {text ? <div className="adjust-size-popover__text--warning">{text}</div> : null}
                    <div className="adjust-size-popover__btn">
                        <Button type="primary" block data-blue onClick={handleBtnClick}>
                            {$t((v) => v.panel.backgroundPanel.tips.add)}
                        </Button>
                    </div>
                </div>
            }
        >
            {children}
        </Popover>
    );
};

export default React.memo(AddPopover);
